export interface ChecklistItem {
  id: string
  label: string
  order: number
  systemId: string
  systemLabel: string
  systemOrder: number
}

const legacySystems = [
  {
    id: '1',
    label: 'Estrutura',
    items: [
      'Trincas na estrutura',
      'Trincas nos braços',
      'Trincas na caçamba/implementos',
      'Soldas e reforços',
      'Pinos e buchas da estrutura',
    ],
  },
  {
    id: '2',
    label: 'Sistema hidráulico',
    items: [
      'Mangueiras',
      'Cilindros',
      'Bomba hidráulica',
      'Comandos hidráulicos',
      'Pressão do sistema',
      'Vazamentos',
    ],
  },
  {
    id: '3',
    label: 'Sistema de combustível',
    items: [
      'Nível do combustível',
      'Filtro de combustível',
      'Bomba de alimentação',
      'Bomba de alta pressão',
      'Unidades injetoras',
      'Linhas e conexões',
      'Vazamentos',
    ],
  },
  {
    id: '4',
    label: 'Motor',
    items: [
      'Nível do óleo',
      'Pressão do óleo',
      'Sistema de arrefecimento',
      'Filtros: óleo, diesel e ar',
      'Correias',
      'Pressão de óleo do motor',
      'Regulagem de válvulas',
      'Chicote elétrico e conectores',
    ],
  },
  {
    id: '5',
    label: 'Transmissão',
    items: [
      'Nível do óleo',
      'Pressão de óleo',
      'Sistema de arrefecimento',
      'Ruídos e vibrações',
      'Funcionamento: avanço e ré',
    ],
  },
  { id: '6', label: 'Freios', items: ['Pressão', 'Bomba', 'Vazamentos', 'Funcionamento'] },
  {
    id: '7',
    label: 'Pneus/Esteiras',
    items: ['Estado geral', 'Desgaste', 'Tensão das esteiras'],
  },
] as const

const version2Systems = legacySystems.map((system) =>
  system.id === '4'
    ? {
        ...system,
        items: [
          'Nível do óleo',
          'Pressão do óleo',
          'Sistema de arrefecimento',
          'Filtro lubrificante',
          'Correias',
          'Pressão de óleo do motor',
          'Chicote elétrico e conectores',
        ],
      }
    : system,
)

const systems = version2Systems.map((system) =>
  system.id === '6'
    ? {
        ...system,
        items: [...system.items, 'Eixo dianteiro', 'Eixo traseiro'],
      }
    : system,
)

function flattenSystems(
  source: readonly { id: string; label: string; items: readonly string[] }[],
): ChecklistItem[] {
  return source.flatMap((system, systemIndex) =>
    system.items.map((label, itemIndex) => ({
      id: `${system.id}.${itemIndex + 1}`,
      label,
      order:
        source.slice(0, systemIndex).reduce((total, current) => total + current.items.length, 0) +
        itemIndex +
        1,
      systemId: system.id,
      systemLabel: system.label,
      systemOrder: systemIndex + 1,
    })),
  )
}

export const CHECKLIST_VERSION = 3

export const LEGACY_CHECKLIST_ITEMS_V1: readonly ChecklistItem[] = flattenSystems(legacySystems)

export const LEGACY_CHECKLIST_ITEMS_V2: readonly ChecklistItem[] = flattenSystems(version2Systems)

export const CHECKLIST_ITEMS: readonly ChecklistItem[] = flattenSystems(systems)

export function checklistItemsForSchemaVersion(schemaVersion: number): readonly ChecklistItem[] {
  if (schemaVersion >= 6) return CHECKLIST_ITEMS
  if (schemaVersion >= 5) return LEGACY_CHECKLIST_ITEMS_V2
  return LEGACY_CHECKLIST_ITEMS_V1
}

export function checklistItemsForResults(
  results: readonly { checklistItemId: string; checklistItemLabel: string }[],
): readonly ChecklistItem[] {
  const hasCurrentBrakeItem = results.some(
    (result) => result.checklistItemId === '6.5' || result.checklistItemId === '6.6',
  )
  if (hasCurrentBrakeItem) return CHECKLIST_ITEMS

  const hasLegacyMotorItem = results.some(
    (result) =>
      result.checklistItemId === '4.8' ||
      result.checklistItemLabel.toLocaleLowerCase('pt-BR').includes('regulagem de válvulas'),
  )
  return hasLegacyMotorItem || results.length === LEGACY_CHECKLIST_ITEMS_V1.length
    ? LEGACY_CHECKLIST_ITEMS_V1
    : LEGACY_CHECKLIST_ITEMS_V2
}

if (
  CHECKLIST_ITEMS.length !== 39 ||
  LEGACY_CHECKLIST_ITEMS_V2.length !== 37 ||
  LEGACY_CHECKLIST_ITEMS_V1.length !== 38
) {
  throw new Error('Os catálogos oficiais precisam conter 39 itens atuais e 37/38 itens legados.')
}
